import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { GraduationCap, ArrowRight, CheckCircle } from 'lucide-react';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { collegeJourneyProgressService } from '../../lib/college-journey-service';
import { collegeAccessControlService } from '../../lib/college-access-control-service';

const CollegeJourneyWidget = ({ isMobile = false }) => {
  const { currentUser, family } = useFamilyContext();
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  
  useEffect(() => {
    const loadProgress = async () => {
      if (!currentUser?.id || !family?.id) return;
      
      try {
        // First check if user has access to college module
        const collegeAccess = await collegeAccessControlService.hasCollegeHubAccess(currentUser.id);
        setHasAccess(collegeAccess);
        
        // If no access, don't load progress
        if (!collegeAccess) {
          setLoading(false);
          return;
        }
        // Try to load from database first
        const dbProgress = await collegeJourneyProgressService.getProgress(currentUser.id);
        
        if (dbProgress) {
          setProgress(dbProgress);
        } else {
          // No database progress found - check localStorage for migration only for this specific user
          const userSpecificKey = `student-onboarding-progress-${currentUser.id}`;
          const onboardingComplete = localStorage.getItem('college-onboarding-complete');
          const onboardingProgress = localStorage.getItem('student-onboarding-progress');
          const userSpecificProgress = localStorage.getItem(userSpecificKey);
          
          // Only migrate if we have user-specific data or if this is the first user accessing the system
          let shouldMigrate = false;
          let currentStep = 1;
          let onboardingIsComplete = false;
          
          if (userSpecificProgress) {
            // User has specific progress data
            try {
              const localProgress = JSON.parse(userSpecificProgress);
              currentStep = localProgress.currentStep || 1;
              onboardingIsComplete = localProgress.onboardingComplete || false;
              shouldMigrate = true;
            } catch (error) {
              console.error('Error parsing user-specific onboarding progress:', error);
            }
          } else if (onboardingComplete || onboardingProgress) {
            // Only migrate general localStorage data if no user-specific data exists
            // and this appears to be the first user (to avoid cross-user contamination)
            const allUserProgress = Object.keys(localStorage).filter(key => 
              key.startsWith('student-onboarding-progress-')
            );
            
            if (allUserProgress.length === 0) {
              // No other users have progress, safe to migrate
              if (onboardingProgress) {
                try {
                  const localProgress = JSON.parse(onboardingProgress);
                  currentStep = localProgress.currentStep || 1;
                } catch (error) {
                  console.error('Error parsing onboarding progress:', error);
                }
              }
              onboardingIsComplete = !!onboardingComplete;
              shouldMigrate = true;
            }
          }
          
          if (shouldMigrate) {
            // Create progress object from localStorage
            const progressData = {
              onboarding_complete: onboardingIsComplete,
              current_step: currentStep,
              total_steps: 4
            };
            
            setProgress(progressData);
            
            // Migrate to database
            try {
              await collegeJourneyProgressService.saveProgress(currentUser.id, {
                onboardingComplete: onboardingIsComplete,
                currentStep,
                totalSteps: 4
              });
              console.log('✅ Migrated college journey progress to database for user:', currentUser.id);
              
              // Store user-specific localStorage for future reference
              localStorage.setItem(userSpecificKey, JSON.stringify({
                currentStep,
                onboardingComplete: onboardingIsComplete
              }));
            } catch (error) {
              console.error('Error migrating progress to database:', error);
            }
          } else {
            // No progress found and no migration needed - user hasn't started
            setProgress({
              onboarding_complete: false,
              current_step: 1,
              total_steps: 4
            });
          }
        }
      } catch (error) {
        console.error('Error loading college journey progress:', error);

        // Try to preserve any existing completion status before falling back
        let fallbackComplete = false;
        try {
          // Check if we can get any existing database record despite the error
          const { data: existingProgress } = await collegeJourneyProgressService.getProgress(currentUser.id);
          fallbackComplete = existingProgress?.onboarding_complete || false;
        } catch (dbError) {
          console.log('Could not check existing progress status');
        }

        // Final fallback - preserve completion status if we found it, otherwise default to false
        setProgress({
          onboarding_complete: fallbackComplete,
          current_step: 1,
          total_steps: 4
        });
      } finally {
        setLoading(false);
      }
    };
    
    loadProgress();
  }, [currentUser?.id, family?.id]);
  
  if (loading) {
    return (
      <div className={`bg-stone-50 border border-stone-200 rounded-xl p-4 ${isMobile ? '' : 'h-full'}`}>
        <div className="animate-pulse flex items-center gap-3">
          <div className="w-10 h-10 bg-stone-200 rounded-lg"></div>
          <div className="flex-1">
            <div className="h-4 bg-stone-200 rounded mb-2"></div>
            <div className="h-3 bg-stone-200 rounded w-3/4"></div>
          </div>
        </div>
      </div>
    );
  }
  
  // If user doesn't have access, don't render anything
  if (!hasAccess) {
    return null;
  }
  
  const currentStep = progress?.current_step || 1;
  const totalSteps = progress?.total_steps || 4;

  if (progress?.onboarding_complete) {
    return (
      <div className={`bg-gradient-to-r from-emerald-50 to-emerald-50 border border-emerald-200 rounded-xl p-4 ${isMobile ? '' : 'h-full'}`}>
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <CheckCircle className="w-5 h-5 text-emerald-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-emerald-900 mb-1">College Journey Complete!</h3>
            <p className="text-sm text-emerald-700 mb-3">
              Your college profile is set up. Access your personalized college hub anytime.
            </p>
            <Link
              to="/college"
              className="inline-flex items-center gap-2 text-sm font-medium text-emerald-700 hover:text-emerald-800 transition-colors"
            >
              Open College Hub
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-gradient-to-r from-azure-50 to-indigo-50 border border-azure-200 rounded-xl p-4 ${isMobile ? '' : 'h-full'}`}>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 bg-azure-100 rounded-lg flex items-center justify-center flex-shrink-0">
          <GraduationCap className="w-5 h-5 text-azure-600" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-azure-900 mb-1">Continue College Journey Setup</h3>
          <p className="text-sm text-azure-700 mb-2">
            Step {currentStep} of {totalSteps} - Your progress is saved
          </p>
          <div className="w-full bg-azure-200 rounded-full h-2 mb-3">
            <div 
              className="bg-azure-600 h-2 rounded-full transition-all duration-300" 
              style={{ width: `${(currentStep / totalSteps) * 100}%` }}
            ></div>
          </div>
          <Link
            to="/college/onboarding"
            className="inline-flex items-center gap-2 text-sm font-medium text-azure-700 hover:text-azure-800 transition-colors"
          >
            Continue Setup
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default CollegeJourneyWidget;